import React, { useContext, useEffect, useState } from 'react'
import TableContainer from '@mui/material/TableContainer'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TablePagination from '@mui/material/TablePagination'
import Paper from '@mui/material/Paper'
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material'
import { PlusIcon } from '../../assets/icons/icons'
import { useNavigate } from 'react-router-dom'
import { useGetQuestions, useMutateDeleteQuestion } from './HrQuestionhook'
import ViewQuestionModal from './ViewQuestionModal'
import { Fetching } from '../../components/Fetching'
import SnackBarContext from '../../context/SnackBarContext'

function HrQuestionsTable() {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [idToDelete, setIdToDelete] = useState(null)
  const [questionIdToDelete, setQuestionIdToDelete] = useState(null)
  const navigate = useNavigate()
  const { data: response, refetch, isLoading } = useGetQuestions('questions')
  const deleteQuestion = useMutateDeleteQuestion('questions')
  const questionsData = response || []
  const { toggleSnackbar } = useContext(SnackBarContext)
  const handleChangePage = (event: any, newPage: any) => {
    setPage(newPage)
  }

  useEffect(() => {
    refetch()
  }, [])
  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }
  const handleDeleteQuestion = (id: any, questionId: any) => {
    setIdToDelete(id)
    setQuestionIdToDelete(questionId)
    setDeleteModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    try {
      await deleteQuestion.mutateAsync({
        id: idToDelete,
        questionId: questionIdToDelete,
      })
      toggleSnackbar('Question deleted successfully', 'success')
      refetch()
    } catch (error) {
      console.error('Error deleting question:', error)
    } finally {
      setDeleteModalOpen(false)
    }
  }

  const handleCancelDelete = () => {
    setDeleteModalOpen(false)
  }

  if (isLoading) {
    return <Fetching />
  }
  return (
    <Box sx={{ marginTop: 1, marginLeft: 2, marginRight: 2 }}>
      <Box
        marginBottom={3}
        alignItems={'center'}
        display={'flex'}
        flexDirection={'row'}
        justifyContent={'space-between'}>
        <Typography
          sx={{
            color: '#2B262C',
            fontSize: '20px',
            fontWeight: 'semibold',
          }}>
          HR Questions
        </Typography>
        <Button
          variant='contained'
          sx={{
            padding: '15px 18px',
            borderRadius: '8px',
            textTransform: 'capitalize',
            fontSize: '13px',
          }}
          onClick={() =>
            navigate('/hr-add-questions', { state: { mode: 'add' } })
          }>
          <PlusIcon /> <Box sx={{ width: 10 }} /> Add Question
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table
          sx={{
            minWidth: 650,
            boxShadow: 'none',
          }}
          aria-label='simple table'>
          <TableHead>
            <TableRow
              sx={{
                '& th': {
                  backgroundColor: '#24212C',
                  padding: '17px',
                  color: 'white',
                  fontWeight: 400,
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                },
              }}>
              <TableCell>Questions</TableCell>
              <TableCell>Question Type</TableCell>
              <TableCell>Answer</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              '& td': {
                backgroundColor: 'white',
                color: '#5F5C5F',
                fontWeight: 400,
                whiteSpace: 'nowrap',
                padding: '18px',
                fontSize: 14,
              },
              '& td:first-child': {
                borderRadius: '13px 0px 0px 14px',
              },
              '& td:last-child': {
                borderRadius: '0px 10px 10px 0px',
              },
            }}>
            {questionsData && questionsData == '' ? (
              <TableRow>
                <TableCell colSpan={12} style={{ textAlign: 'center' }}>
                  No HR Question Found
                </TableCell>
              </TableRow>
            ) : (
              questionsData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((dataItem: any, index: number) => (
                  <React.Fragment key={dataItem._id}>
                    {dataItem.questions.map((question: any, qIndex: number) => (
                      <TableRow
                        key={`${dataItem._id}-${qIndex}`}
                        sx={{
                          '& td, & th': {
                            textAlign: 'center',
                            background: index % 2 === 0 ? 'white' : '#F1EFF5',
                          },
                        }}>
                        <TableCell>
                          <Box
                            component={TextField}
                            multiline
                            sx={{
                              height: '100%',
                              maxHeight: '50px',
                              overflow: 'auto',
                              width: '100%',
                              ml: 1,
                              textWrap: 'wrap',
                              '& :hover': {
                                cursor: 'default',
                              },
                            }}
                            variant='standard'
                            value={question.questionText}
                            InputProps={{
                              readOnly: true,
                              disableUnderline: true,
                            }}
                          />
                        </TableCell>
                        <TableCell>{question.questionType}</TableCell>
                        <TableCell>
                          <Box
                            component={TextField}
                            multiline
                            sx={{
                              maxHeight: '50px',
                              width: '100%',
                              overflow: 'auto',
                              '& .MuiInputBase-input': {
                                textAlign: 'center',
                                cursor: 'default',
                              },
                            }}
                            variant='standard'
                            value={
                              question.questionType === 'Yes/No'
                                ? question.correctAnswer?.toString() == 'true'
                                  ? 'Yes'
                                  : 'No'
                                : question.correctAnswer
                            }
                            InputProps={{
                              readOnly: true,
                              disableUnderline: true,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box>
                            <ViewQuestionModal data={question} />
                            <Button
                              sx={{
                                ml: 2,
                                padding: '1.5px 12px',
                                textTransform: 'capitalize',
                                borderRadius: 2,
                                boxShadow: 'none',
                                minWidth: 0,
                                backgroundColor: '#59A0A0CC',
                                '&:hover': {
                                  backgroundColor: '#59A0A0CC',
                                  boxShadow: 'none',
                                },
                                fontSize: 13,
                              }}
                              variant='contained'
                              color='secondary'
                              onClick={() =>
                                navigate('/hr-add-questions', {
                                  state: {
                                    data: question,
                                    questionId: dataItem._id,
                                    mode: 'edit',
                                  },
                                })
                              }>
                              Edit
                            </Button>
                            <Button
                              sx={{
                                padding: '1.5px 12px',
                                textTransform: 'capitalize',
                                borderRadius: 2,
                                boxShadow: 'none',
                                backgroundColor: '#8E221880',
                                '&:hover': {
                                  backgroundColor: '#8E221880',
                                  boxShadow: 'none',
                                },
                                minWidth: 0,
                                fontSize: 13,
                                ml: 2,
                              }}
                              variant='contained'
                              onClick={() =>
                                handleDeleteQuestion(dataItem._id, question._id)
                              }>
                              Delete
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component='div'
          count={questionsData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteModalOpen}
        sx={{
          '& .css-yiavyu-MuiBackdrop-root-MuiDialog-backdrop ': {
            backgroundColor: 'rgba(0,0,0,0.7)',
          },
        }}
        onClose={handleCancelDelete}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'>
        <DialogTitle id='alert-dialog-title'>Delete Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            Are you sure you want to delete this question?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelDelete}
            sx={{
              '&:hover': {
                backgroundColor: '#f0f0f0',
              },
            }}
            color='primary'>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            sx={{
              '&:hover': {
                backgroundColor: '#f0f0f0',
              },
            }}
            color='primary'
            autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default HrQuestionsTable
